///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Tunnel, TunnelReportDTO } from '../../../../generated/shared-types.model';
import { GtthHistoryService } from '../../../logical-topology/engine/history-service';
import { TunnelModel } from './models/tunnel.model';
import { combineLatestWith, map, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { isDefined } from '@siklu/sui-lib/utils';
import { TunnelOffset } from '../../../logical-topology/entities/tunnel/view/tunnel-view';
import { ExtendedTunnelReportDTO } from './models/extended-tunnel-report.model';

@Injectable()
export class GtthTunnelsService implements OnDestroy {
    private static readonly ALL_TUNNELS_ID = -1;
    public areAllTunnelsDisplayed = false;
    private readonly filterSubject$: BehaviorSubject<string>;
    private readonly viewTunnelSubject$: BehaviorSubject<{ tunnelId: number; isShow: boolean }>;

    private tunnelsSubject: ReplaySubject<ExtendedTunnelReportDTO>;

    constructor(
        private readonly http: HttpClient,
        private readonly historyService: GtthHistoryService
    ) {
        this.filterSubject$ = new BehaviorSubject<string>('');
        this.viewTunnelSubject$ = new BehaviorSubject<{ tunnelId: number; isShow: boolean }>({
            tunnelId: GtthTunnelsService.ALL_TUNNELS_ID,
            isShow: false
        });
    }

    public getTunnels(force = false): Observable<ExtendedTunnelReportDTO> {
        if (force || !this.tunnelsSubject) {
            if (!this.tunnelsSubject) {
                this.tunnelsSubject = new ReplaySubject<ExtendedTunnelReportDTO>(1);
            }

            const projectId = this.historyService.projectStateService.getProjectId();
            this.http.get<TunnelReportDTO>(`/api/reports/${projectId}/tunnels`).subscribe((report: TunnelReportDTO) => {
                const extendedReport = new ExtendedTunnelReportDTO();
                extendedReport.buLocationsWithoutTunnels = report.buLocationsWithoutTunnels;
                extendedReport.buLocationsWithSingleTunnel = report.buLocationsWithSingleTunnel;
                extendedReport.buMustLocationsWithoutBuDevices = report.buMustLocationsWithoutBuDevices;
                extendedReport.tunnelModels = report.tunnels.map((tunnel: Tunnel) => new TunnelModel(tunnel, this.historyService));
                this.tunnelsSubject.next(extendedReport);
            });
        }

        return this.tunnelsSubject.asObservable();
    }

    public updateFilter(filterText: string): void {
        this.filterSubject$.next(filterText);
    }

    public toggleAllTunnelsView(): void {
        this.viewTunnelSubject$.next({
            isShow: !this.areAllTunnelsDisplayed,
            tunnelId: GtthTunnelsService.ALL_TUNNELS_ID
        });
        // send undefined event to avoid duplications after filtering
        this.viewTunnelSubject$.next(undefined);
    }

    public toggleTunnelView(tunnel: TunnelModel): void {
        this.viewTunnelSubject$.next({
            isShow: !tunnel.isShown(),
            tunnelId: tunnel.id
        });
        // send undefined event to avoid duplications after filtering
        this.viewTunnelSubject$.next(undefined);
    }

    public clearTunnels(): void {
        this.filterSubject$.next('');
        this.viewTunnelSubject$.next({
            tunnelId: GtthTunnelsService.ALL_TUNNELS_ID,
            isShow: false
        });
        if (this.tunnelsSubject) {
            this.tunnelsSubject.asObservable().subscribe((report: ExtendedTunnelReportDTO) => {
                report.tunnelModels.forEach((tunnel) => tunnel.hide());
            });
            this.tunnelsSubject.complete();
            this.tunnelsSubject = undefined;
        }
    }

    ngOnDestroy(): void {
        this.clearTunnels();
    }

    getTunnelsWithTypes(): Observable<TunnelModel[]> {
        return this.getTunnels().pipe(
            combineLatestWith(this.filterSubject$),
            map(([report, filterText]) => this.getTunnelsFilterMap(report.tunnelModels, filterText)),

            combineLatestWith(this.viewTunnelSubject$),
            tap(([tunnelsFilterMap, tunnelToShow]) => {
                // skip duplicated event
                if (isDefined(tunnelToShow)) {
                    this.showTunnels(tunnelsFilterMap, tunnelToShow);
                }
            }),
            map(([tunnelsFilterMap]) => tunnelsFilterMap.get(true)),
            tap((tunnels: TunnelModel[]) => (this.areAllTunnelsDisplayed = tunnels.every((tunnel) => tunnel.isShown())))
        );
    }

    getLocationNames(): Observable<ExtendedTunnelReportDTO> {
        return this.getTunnels().pipe(
            combineLatestWith(this.filterSubject$),
            map(([report, filterText]) => {
                const filteredReport = new ExtendedTunnelReportDTO();
                const searchRegExp = new RegExp(filterText, 'gi');

                filteredReport.buLocationsWithSingleTunnel = report.buLocationsWithSingleTunnel.filter(
                    (location) => location.search(searchRegExp) !== -1
                );
                filteredReport.buLocationsWithoutTunnels = report.buLocationsWithoutTunnels.filter(
                    (location) => location.search(searchRegExp) !== -1
                );
                filteredReport.buMustLocationsWithoutBuDevices = report.buMustLocationsWithoutBuDevices.filter(
                    (location) => location.search(searchRegExp) !== -1
                );
                filteredReport.tunnelModels = report.tunnelModels;

                return filteredReport;
            })
        );
    }

    getTunnelsFilterMap(tunnels: TunnelModel[], filterText: string): Map<boolean, TunnelModel[]> {
        const searchRegExp = new RegExp(filterText, 'gi');

        const tunnelsMap = new Map<boolean, TunnelModel[]>([
            [true, []],
            [false, []]
        ]);

        tunnels.forEach((tunnel: TunnelModel) => {
            const isMatch = [`${tunnel.id}`, tunnel.tunnelNodeNames[0], tunnel.tunnelNodeNames[tunnel.tunnelNodeNames.length - 1]].some(
                (value) => value.search(searchRegExp) !== -1
            );
            tunnelsMap.get(isMatch).push(tunnel);
        });

        return tunnelsMap;
    }

    private showTunnels(
        tunnelsFilterMap: Map<boolean, TunnelModel[]>,
        tunnelToShow: {
            tunnelId: number;
            isShow: boolean;
        }
    ): void {
        // hide filtered out tunnels
        tunnelsFilterMap.get(false).forEach((tunnel: TunnelModel) => tunnel.hide());

        let tunnelsToRecalculate = [];

        if (tunnelToShow.tunnelId === GtthTunnelsService.ALL_TUNNELS_ID) {
            if (tunnelToShow.isShow) {
                tunnelsToRecalculate = tunnelsFilterMap.get(true);
            } else {
                tunnelsFilterMap.get(true).forEach((tunnel: TunnelModel) => tunnel.hide());
                // all tunnels are hidden, nothing to recalculate
                return;
            }
        } else {
            // add all already shown tunnels to recalculation
            tunnelsFilterMap.get(true).forEach((tunnel) => {
                if (tunnel.id === tunnelToShow.tunnelId) {
                    if (tunnelToShow.isShow) {
                        tunnelsToRecalculate.push(tunnel);
                    } else {
                        tunnel.hide();
                    }
                } else if (tunnel.isShown()) {
                    tunnelsToRecalculate.push(tunnel);
                }
            });
        }

        this.recalculateTunnelCoordinates(tunnelsToRecalculate);
    }

    private recalculateTunnelCoordinates(tunnels: TunnelModel[]): void {
        const tunnelPathCount = new Map<number, number>();
        const tunnelAssignedPathCount = new Map<number, number>();

        // longest tunnels goes first
        tunnels = tunnels.sort((a, b) => b.tunnelNodes.length - a.tunnelNodes.length);

        // calculate total count of tunnels for each node <-> node link
        tunnels.forEach((tunnel) => {
            for (let i = 0; i < tunnel.tunnelNodes.length - 1; i++) {
                const nodeToNodeId = this.calculateNodeToNodeLinkId(tunnel.tunnelNodes[i].getId(), tunnel.tunnelNodes[i + 1].getId());
                if (!tunnelPathCount.has(nodeToNodeId)) {
                    tunnelPathCount.set(nodeToNodeId, 1);
                } else {
                    tunnelPathCount.set(nodeToNodeId, tunnelPathCount.get(nodeToNodeId) + 1);
                }
                tunnelAssignedPathCount.set(nodeToNodeId, 1);
            }
        });

        // add tunnel offset for each node <-> node link
        tunnels.forEach((tunnel) => {
            const tunnelOffsets: TunnelOffset[] = [];
            for (let i = 0; i < tunnel.tunnelNodes.length - 1; i++) {
                const nodeFrom = tunnel.tunnelNodes[i].getId();
                const nodeTo = tunnel.tunnelNodes[i + 1].getId();
                const nodeToNodeId = this.calculateNodeToNodeLinkId(nodeFrom, nodeTo);
                const totalCountOfTunnels = tunnelPathCount.get(nodeToNodeId);
                const tunnelOffset = tunnelAssignedPathCount.get(nodeToNodeId);
                tunnelOffsets[i] = { total: totalCountOfTunnels, offset: tunnelOffset } as TunnelOffset;
                tunnelAssignedPathCount.set(nodeToNodeId, tunnelAssignedPathCount.get(nodeToNodeId) + 1);
            }

            tunnel.setTunnelOffsets(tunnelOffsets);
        });

        tunnels.forEach((tunnel) => {
            tunnel.draw(this.historyService);
        });
    }

    private calculateNodeToNodeLinkId(node1Id: number, node2Id: number): number {
        return Math.max(node1Id, node2Id) * Math.max(node1Id, node2Id) + Math.min(node1Id, node2Id);
    }
}
